import { Alert, AlertIcon, AlertText } from "@/components/ui/alert";
import { InfoIcon } from "@/components/ui/icon";
import { Pressable } from "@/components/ui/pressable";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { UserPayoutDTOFee } from "@/generated/api/models";
import { Link as ExpoLink } from "expo-router";

export const WalletPayoutAlerts = {
  MaximumExceeded: function MaximumExceeded({
    formatCents,
    max,
  }: {
    formatCents: (cents: number) => string;
    max: number;
  }) {
    return (
      <Alert action="error" variant="solid">
        <AlertIcon as={InfoIcon} className="min-w-5" />
        <AlertText>
          Please check amount. You may request payout up to {formatCents(max)}
        </AlertText>
      </Alert>
    );
  },
  InsufficientBalance: function InsufficientBalance({
    formatCents,
    min,
  }: {
    formatCents: (cents: number) => string;
    min: number;
  }) {
    return (
      <Alert action="error" variant="solid">
        <AlertIcon as={InfoIcon} className="min-w-5" />
        <AlertText>
          Too low amount. Unfortunately, we cannot process payouts for amounts
          less than {formatCents(min)}
        </AlertText>
      </Alert>
    );
  },
  EarlyPayout: function EarlyPayout({
    formatCents,
    available,
    onAvailablePress,
  }: {
    formatCents: (cents: number) => string;
    available: number;
    onAvailablePress: () => void;
  }) {
    return (
      <Alert action="info" variant="solid">
        <AlertIcon as={InfoIcon} className="min-w-5" />
        <AlertText>
          You're requesting more funds, than currently available for payout (
          <AlertText bold>{formatCents(available)}</AlertText>
          ). We will try to fulfill your request, the final amount to be
          confirmed on the next step.
        </AlertText>
      </Alert>
    );
  },
  ZeroAmount: function ZeroAmount() {
    return (
      <Alert action="error" variant="solid">
        <AlertIcon as={InfoIcon} className="min-w-5" />
        <AlertText>
          Unable to fulfill your early payout request due to limits. Feel free
          to try later, or after funds processing completed.
        </AlertText>
      </Alert>
    );
  },
  AmountChanged: function AmountChanged() {
    return (
      <Alert action="warning" variant="solid">
        <AlertIcon as={InfoIcon} className="min-w-5" />
        <AlertText>
          We are able to process your early payout request only with partial
          amount at the moment. You may continue or try again later.
        </AlertText>
      </Alert>
    );
  },
  ZeroFee: function ZeroFee() {
    return (
      <Alert action="info" variant="solid">
        <AlertIcon as={InfoIcon} className="min-w-5" />
        <AlertText>We do not charge any fee for this payout.</AlertText>
      </Alert>
    );
  },
  NonZeroFee: function NonZeroFee({
    fee,
    formatCents,
  }: {
    fee: UserPayoutDTOFee;
    formatCents: (cents: number) => void;
  }) {
    const helpLink = "https://help.gotips.co/getting-started/fees-and-charges/";

    return (
      <Alert action="info" variant="solid">
        <AlertIcon as={InfoIcon} className="min-w-5" />
        <VStack className="gap-2">
          <AlertText>
            {fee.reason?.description ? (
              <>{fee.reason.description}</>
            ) : (
              <>{formatCents(fee.amount)} is applied to this transaction.</>
            )}
          </AlertText>
          <ExpoLink href={helpLink} target="_blank">
            <AlertText underline>Learn more about payout fees</AlertText>
          </ExpoLink>
        </VStack>
      </Alert>
    );
  },
};
